import React from 'react';
import { motion } from 'framer-motion';

const BuyersRemorseTimelineLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Buyer's Remorse Timeline: How Purchase Guilt Evolves
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Understand & Overcome Your Purchase Timeline Guilt</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              From pre-purchase excitement to post-purchase shame, track how your shopping emotions change over time and learn to break the guilt cycle.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Purchase Timeline
            </motion.button>
          </div>
          <div className="text-8xl">⏱️</div>
        </div>
      </div>
      
      {/* Buyer's Remorse Timeline Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-6 dark:text-white">The 6 Stages of Buyer's Remorse</h2>
        
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute h-full w-0.5 bg-gray-200 dark:bg-gray-600 left-6 md:left-1/2 transform -translate-x-1/2 z-0"></div>
          
          {/* Timeline Items */}
          <div className="relative z-10 space-y-8">
            {/* Stage 1 */}
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 md:pr-10 md:text-right order-2 md:order-1">
                <h3 className="text-xl font-bold dark:text-white">Pre-Purchase Excitement</h3>
                <p className="text-gray-600 dark:text-gray-300 mt-2">
                  Your brain floods with dopamine at the prospect of acquiring something new. Logic takes a backseat as emotional anticipation builds.
                </p>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">0-48 hours before purchase</div>
              </div>
              <div className="md:w-1/2 flex justify-center md:justify-start order-1 md:order-2">
                <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-xl border-4 border-white dark:border-gray-800">
                  😍
                </div>
              </div>
            </div>
            
            {/* Stage 2 */}
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 flex justify-center md:justify-end order-1">
                <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center text-xl border-4 border-white dark:border-gray-800">
                  🛒
                </div>
              </div>
              <div className="md:w-1/2 md:pl-10 order-2">
                <h3 className="text-xl font-bold dark:text-white">Purchase High</h3>
                <p className="text-gray-600 dark:text-gray-300 mt-2">
                  The moment of purchase creates a rush of satisfaction. You've secured the item, and the reward centers in your brain are fully activated.
                </p>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Purchase moment</div>
              </div>
            </div>
            
            {/* Stage 3 */}
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 md:pr-10 md:text-right order-2 md:order-1">
                <h3 className="text-xl font-bold dark:text-white">Initial Doubt</h3>
                <p className="text-gray-600 dark:text-gray-300 mt-2">
                  The first traces of uncertainty emerge. You begin questioning whether you actually needed the item and if you made the right choice.
                </p>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">1-24 hours after purchase</div>
              </div>
              <div className="md:w-1/2 flex justify-center md:justify-start order-1 md:order-2">
                <div className="h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center text-xl border-4 border-white dark:border-gray-800">
                  🤔
                </div>
              </div>
            </div>
            
            {/* Stage 4 */}
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 flex justify-center md:justify-end order-1">
                <div className="h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center text-xl border-4 border-white dark:border-gray-800">
                  😰
                </div>
              </div>
              <div className="md:w-1/2 md:pl-10 order-2">
                <h3 className="text-xl font-bold dark:text-white">Full Remorse</h3>
                <p className="text-gray-600 dark:text-gray-300 mt-2">
                  Reality sets in and guilt peaks. Financial anxiety, concerns about necessity, and comparisons to alternatives intensify your regret.
                </p>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">2-7 days after purchase</div>
              </div>
            </div>
            
            {/* Stage 5 */}
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 md:pr-10 md:text-right order-2 md:order-1">
                <h3 className="text-xl font-bold dark:text-white">Rationalization</h3>
                <p className="text-gray-600 dark:text-gray-300 mt-2">
                  You begin creating justifications for the purchase. Your brain works to reduce cognitive dissonance by finding reasons why the purchase was actually reasonable.
                </p>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">1-2 weeks after purchase</div>
              </div>
              <div className="md:w-1/2 flex justify-center md:justify-start order-1 md:order-2">
                <div className="h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center text-xl border-4 border-white dark:border-gray-800">
                  🤷
                </div>
              </div>
            </div>
            
            {/* Stage 6 */}
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 flex justify-center md:justify-end order-1">
                <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center text-xl border-4 border-white dark:border-gray-800">
                  🔄
                </div>
              </div>
              <div className="md:w-1/2 md:pl-10 order-2">
                <h3 className="text-xl font-bold dark:text-white">Acceptance & Reset</h3>
                <p className="text-gray-600 dark:text-gray-300 mt-2">
                  The emotional intensity fades. You either make peace with the purchase or move on, but the experience shapes future buying behavior—for better or worse.
                </p>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">2+ weeks after purchase</div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* Guilt Intensity Graph */}
      <motion.section 
        className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5"
        variants={itemVariants}
      >
        <h2 className="text-xl font-bold mb-4 dark:text-white">Buyer's Remorse Intensity Over Time</h2>
        
        <div className="relative h-64 mb-4">
          {/* Y-Axis Label */}
          <div className="absolute transform -rotate-90 -translate-y-1/2 -left-10 top-1/2 text-sm text-gray-500 dark:text-gray-400">
            Guilt Intensity
          </div>
          
          {/* X-Axis */}
          <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-300 dark:bg-gray-500"></div>
          
          {/* Y-Axis */}
          <div className="absolute top-0 left-0 h-full w-0.5 bg-gray-300 dark:bg-gray-500"></div>
          
          {/* Graph Line */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path 
              d="M0,80 C10,70 15,20 25,15 C35,10 45,90 55,95 C65,90 75,60 85,40 C95,30 100,40 100,60" 
              fill="none" 
              stroke="#3b82f6" 
              strokeWidth="3"
              className="dark:stroke-blue-400"
            />
          </svg>
          
          {/* Points on timeline */}
          <div className="absolute bottom-0 left-0 w-full flex justify-between px-4">
            <div className="text-center">
              <div className="h-2 w-0.5 bg-gray-300 dark:bg-gray-500 mb-1 mx-auto"></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">Pre-<br/>Purchase</span>
            </div>
            <div className="text-center">
              <div className="h-2 w-0.5 bg-gray-300 dark:bg-gray-500 mb-1 mx-auto"></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">Purchase<br/>Moment</span>
            </div>
            <div className="text-center">
              <div className="h-2 w-0.5 bg-gray-300 dark:bg-gray-500 mb-1 mx-auto"></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">24<br/>Hours</span>
            </div>
            <div className="text-center">
              <div className="h-2 w-0.5 bg-gray-300 dark:bg-gray-500 mb-1 mx-auto"></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">1<br/>Week</span>
            </div>
            <div className="text-center">
              <div className="h-2 w-0.5 bg-gray-300 dark:bg-gray-500 mb-1 mx-auto"></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">2<br/>Weeks</span>
            </div>
            <div className="text-center">
              <div className="h-2 w-0.5 bg-gray-300 dark:bg-gray-500 mb-1 mx-auto"></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">1<br/>Month</span>
            </div>
          </div>
          
          {/* Key points on graph */}
          <div className="absolute left-[25%] top-[15%] flex flex-col items-center">
            <div className="h-4 w-4 rounded-full bg-blue-500 dark:bg-blue-400 mb-1"></div>
            <span className="text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 px-1 rounded">Peak Excitement</span>
          </div>
          
          <div className="absolute left-[55%] top-[95%] flex flex-col items-center">
            <div className="h-4 w-4 rounded-full bg-red-500 dark:bg-red-400 mb-1"></div>
            <span className="text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 px-1 rounded">Maximum Guilt</span>
          </div>
          
          <div className="absolute left-[85%] top-[40%] flex flex-col items-center">
            <div className="h-4 w-4 rounded-full bg-green-500 dark:bg-green-400 mb-1"></div>
            <span className="text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 px-1 rounded">Emotional Resolution</span>
          </div>
        </div>
        
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          Buyer's remorse typically peaks 3-5 days after purchase before gradually subsiding
        </div>
      </motion.section>
      
      {/* Factors Affecting Your Remorse Timeline */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">What Affects Your Remorse Timeline?</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💰</span>
              <div>
                <h3 className="font-bold dark:text-white">Purchase Amount</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Higher price tags intensify guilt and extend your remorse timeline, often lasting 3-5 times longer than for small purchases.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🧠</span>
              <div>
                <h3 className="font-bold dark:text-white">Personal Financial Anxiety</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Your existing relationship with money can amplify guilt feelings, making even small purchases trigger disproportionate remorse.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📦</span>
              <div>
                <h3 className="font-bold dark:text-white">Product Usage</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Items that get immediate, regular use show faster guilt reduction compared to purchases that remain unused or underutilized.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">👥</span>
              <div>
                <h3 className="font-bold dark:text-white">Social Validation</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Positive feedback from others can cut remorse duration by 70%, while negative comments can double your guilt timeline.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🔄</span>
              <div>
                <h3 className="font-bold dark:text-white">Return Policy</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Flexible return options reduce initial remorse but can paradoxically extend the decision anxiety phase as you debate keeping the item.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🛒</span>
              <div>
                <h3 className="font-bold dark:text-white">Purchase Context</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Impulse buys typically generate 40% more guilt than planned purchases, with midnight shopping showing the highest remorse rates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* Category-Specific Timelines */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Buyer's Remorse By Purchase Category</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <th className="px-4 py-2 text-left dark:text-white">Purchase Type</th>
                <th className="px-4 py-2 text-left dark:text-white">Guilt Peak Timing</th>
                <th className="px-4 py-2 text-left dark:text-white">Resolution Timeline</th>
                <th className="px-4 py-2 text-left dark:text-white">Unique Factor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              <tr className="bg-white dark:bg-gray-800">
                <td className="px-4 py-3 dark:text-white">Fashion & Clothing</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">2-3 days after purchase</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">1-2 weeks</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">First wear significantly reduces guilt</td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <td className="px-4 py-3 dark:text-white">Tech & Electronics</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">5-7 days after purchase</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">2-4 weeks</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">New model announcements can re-trigger guilt</td>
              </tr>
              <tr className="bg-white dark:bg-gray-800">
                <td className="px-4 py-3 dark:text-white">Home Décor</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">At installation/placement</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">1-3 months</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">Visitor compliments accelerate resolution</td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <td className="px-4 py-3 dark:text-white">Luxury Items</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">Immediately after purchase</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">3-6 months</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">Guilt often returns during financial stress</td>
              </tr>
              <tr className="bg-white dark:bg-gray-800">
                <td className="px-4 py-3 dark:text-white">Subscription Services</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">At first renewal</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">Cycles with each billing</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">Low usage months spike remorse</td>
              </tr>
            </tbody>
          </table>
        </div>
      </motion.section>
      
      {/* How Our Tool Helps */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Remorse Timeline Tracker Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Map Your Personal Guilt Timeline
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Track how your feelings about purchases evolve over specific time periods, identifying your unique remorse patterns.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Predict Future Remorse Cycles
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Our algorithm analyzes your purchase history to forecast how you'll feel at each stage of ownership for new purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Receive Stage-Specific Interventions
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get personalized strategies tailored to each phase of buyer's remorse, from pre-purchase planning to post-purchase validation.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Shorten Your Guilt Cycle
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Learn techniques to reduce the intensity and duration of remorse through mindful purchasing and post-purchase practices.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* Timeline Intervention Stats */}
      <motion.section 
        className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5"
        variants={itemVariants}
      >
        <h2 className="text-xl font-bold mb-4 dark:text-white">The Impact of Timeline Interventions</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">73%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">reduction in peak guilt intensity with pre-purchase planning</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">64%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">shorter remorse duration with usage tracking</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">82%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of users report better purchase decisions after timeline tracking</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">3.5x</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">increase in purchase satisfaction after implementing remorse strategies</p>
          </div>
        </div>
      </motion.section>
      
      {/* Testimonials section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">From Remorse to Relief: Success Stories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to spend weeks agonizing over purchases, with guilt peaking around day 5. By tracking my timeline, I learned to implement intervention strategies exactly when my remorse spikes."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Daniel R.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Cut remorse duration by 70%</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I discovered my guilt cycle was entirely predictable. Now I plan usage activities for days 3-7 after big purchases—exactly when my remorse would normally peak—and I barely feel guilty anymore."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Sophia L.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Mindful Timeline Shopper</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* FAQ Section - important for SEO */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Buyer's Remorse Timeline</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How long does buyer's remorse typically last?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              The timeline varies based on purchase type and personal factors, but most buyer's remorse follows a 2-3 week cycle. Small purchases may resolve in days, while major purchases like homes or cars can trigger remorse cycles lasting months. Our tracking tool helps you identify your personal timeline.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I feel fine right after buying, but guilty a few days later?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              This common pattern occurs because the immediate dopamine rush of purchasing masks negative emotions. Once this chemical reward fades (typically within 24-48 hours), your analytical brain begins evaluating the purchase more objectively. Our timeline tracker helps you prepare for this predictable emotional shift.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is there a way to speed up the remorse cycle?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Yes! Our data shows several effective interventions: immediate, purposeful use of the item can reduce remorse duration by 64%; sharing your purchase with supportive friends cuts guilt intensity by 38%; and implementing a pre-purchase evaluation process prevents the deepest remorse spikes altogether.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I sometimes re-experience guilt months after a purchase?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              These "guilt echoes" typically occur when: you encounter financial stress that makes past spending seem imprudent; you see the item unused or underutilized; you discover a better alternative or price; or during seasonal events like bill-paying or tax time. Our timeline tracking identifies your personal guilt trigger points.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* CTA section */}
      <motion.section 
        className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Master Your Remorse Timeline?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Track your purchase emotions over time and discover personalized interventions for every stage of the buyer's remorse cycle.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Start Tracking Your Timeline Now
        </motion.button>
      </motion.section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">Understanding Buyer's Remorse Timeline</h2>
          <p className="mb-3">
            Buyer's remorse timeline refers to the predictable emotional journey consumers experience after making purchases. This timeline typically progresses through distinct stages: pre-purchase excitement, purchase high, initial doubt, peak remorse, rationalization, and finally, acceptance.
          </p>
          <p className="mb-3">
            Our Buyer's Remorse Timeline Tracker helps you understand and navigate these emotional stages by mapping your personal guilt patterns, predicting when your remorse will peak, and providing stage-specific interventions to reduce both the intensity and duration of shopping guilt.
          </p>
          <p>
            Whether you're dealing with post-purchase anxiety, delayed guilt that emerges days after buying, or recurring remorse about past purchases, our free tool helps you develop a healthier relationship with your buying decisions and break the cycle of purchase regret once and for all.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default BuyersRemorseTimelineLanding;
